import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Building2, CalendarDays, MapPin, GraduationCap, CheckCircle2,
         Clock, UserMinus, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const TRACK_TONE = {
  'Live Class': { ring: 'border-amber-500/50', chip: 'bg-amber-500/15 text-amber-200 border-amber-400/30' },
  'Webinar':    { ring: 'border-blue-500/50',  chip: 'bg-blue-500/15 text-blue-200 border-blue-400/30' },
  'CE':         { ring: 'border-emerald-500/50', chip: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30' },
};

const formatWhen = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const SessionCard = ({ session, registered, onRegister, onUnregister, busy }) => {
  const tone = TRACK_TONE[session.track] || TRACK_TONE['Webinar'];
  return (
    <Card className={`bg-[#131A2A] ${tone.ring}`} data-testid={`nu-session-${session.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <span className={`text-[10px] font-mono uppercase tracking-[0.18em] border rounded px-2 py-0.5 ${tone.chip}`}>
            {session.track}
          </span>
          <span className="text-[10px] font-mono text-slate-500 uppercase">{session.id}</span>
        </div>
        <h4 className="text-base font-bold text-white mt-2.5">{session.title}</h4>
        <p className="text-xs text-slate-400 mt-1.5 line-clamp-3">{session.summary}</p>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono">
          <div className="flex items-center gap-1.5 text-slate-300">
            <GraduationCap className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
            <span className="truncate">{session.lecturer}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-300">
            <CalendarDays className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
            <span className="truncate">{formatWhen(session.scheduled_for)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-300">
            <MapPin className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
            <span className="truncate">{session.format}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-300">
            <Clock className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
            <span className="truncate">{session.duration_min} min · {session.ce_hours} CE</span>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-slate-700/60">
          {registered ? (
            <div className="flex items-center gap-2">
              <span className="flex-1 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300">
                <CheckCircle2 className="w-4 h-4" /> Registered
              </span>
              <button
                onClick={() => onUnregister(session)}
                disabled={busy}
                className="text-xs font-mono uppercase tracking-wider text-rose-300 hover:text-rose-200 inline-flex items-center gap-1 border border-rose-500/40 hover:border-rose-400 rounded px-2.5 py-1 disabled:opacity-50"
                data-testid={`nu-unregister-${session.id}`}
              >
                <UserMinus className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => onRegister(session)}
              disabled={busy}
              className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-1.5"
              data-testid={`nu-register-${session.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
              Register
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const NexusUniversityPanel = () => {
  const [catalog, setCatalog] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [filter, setFilter] = useState('ALL');

  const loadAll = async () => {
    try {
      const [c, r] = await Promise.all([
        apiCall('/api/training-center/nexus-university'),
        apiCall('/api/training-center/nexus-university/registrations'),
      ]);
      setCatalog(c.catalog || []);
      setRegistrations(r.registrations || []);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, r] = await Promise.all([
          apiCall('/api/training-center/nexus-university'),
          apiCall('/api/training-center/nexus-university/registrations'),
        ]);
        if (!alive) return;
        setCatalog(c.catalog || []);
        setRegistrations(r.registrations || []);
      } catch (e) { if (alive) toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const isRegistered = (sid) => registrations.some(r => r.session_id === sid);

  const register = async (s) => {
    setBusyId(s.id);
    try {
      await apiCall(`/api/training-center/nexus-university/${s.id}/register`, { method: 'POST' });
      toast.success(`Registered for ${s.title}`);
      await loadAll();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(''); }
  };

  const unregister = async (s) => {
    setBusyId(s.id);
    try {
      await apiCall(`/api/training-center/nexus-university/${s.id}/register`, { method: 'DELETE' });
      toast.success(`Cancelled registration for ${s.title}`);
      await loadAll();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(''); }
  };

  const filtered = useMemo(() => {
    if (filter === 'ALL') return catalog;
    if (filter === 'REGISTERED') return catalog.filter(s => isRegistered(s.id));
    return catalog.filter(s => s.track === filter);
  }, [catalog, filter, registrations]);

  const totalCe = useMemo(
    () => registrations.reduce((sum, r) => {
      const s = catalog.find(c => c.id === r.session_id);
      return sum + (s?.ce_hours || 0);
    }, 0),
    [registrations, catalog],
  );

  const filters = ['ALL', 'Live Class', 'Webinar', 'CE', 'REGISTERED'];

  return (
    <div className="space-y-4" data-testid="nexus-university-panel">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="nu-summary">
        <div className="rounded-lg border-2 border-amber-400/30 bg-amber-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">Sessions</p>
          <p className="text-xl font-bold text-white mt-0.5">{catalog.length}</p>
        </div>
        <div className="rounded-lg border-2 border-emerald-400/30 bg-emerald-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Registered</p>
          <p className="text-xl font-bold text-white mt-0.5" data-testid="nu-registered-count">{registrations.length}</p>
        </div>
        <div className="rounded-lg border-2 border-blue-400/30 bg-blue-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-blue-300">CE Hours Reserved</p>
          <p className="text-xl font-bold text-white mt-0.5">{totalCe.toFixed(2)}</p>
        </div>
        <div className="rounded-lg border-2 border-slate-500/30 bg-slate-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-300">Annual Tracking</p>
          <p className="text-xl font-bold text-white mt-0.5">Texas PSB</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="nu-filters">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs font-mono uppercase tracking-wider rounded px-2.5 py-1 border ${
              filter === f
                ? 'bg-amber-500 border-amber-400 text-slate-900 font-bold'
                : 'bg-[#0E1626] border-slate-700 text-slate-300 hover:border-amber-500/40'
            }`}
            data-testid={`nu-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Catalog */}
      {loading ? (
        <p className="text-slate-400 italic">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 italic text-sm">No sessions match this filter.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="nu-catalog">
          {filtered.map(s => (
            <SessionCard
              key={s.id}
              session={s}
              registered={isRegistered(s.id)}
              onRegister={register}
              onUnregister={unregister}
              busy={busyId === s.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NexusUniversityPanel;
